/**
 * Human-readable review rows for the Apprise VM, grouped like the Azure portal tabs
 * (Basics · Disks · Networking · Management · Monitoring · Tags). Shared by the wizard's
 * Review & Deploy step and the deployment detail page ("resolved configuration").
 *
 * Built from the payload only, so a password can never appear: the credential row
 * says "Default password (configured by your administrator)" or "Custom password (hidden)".
 */
import type { AppriseExecutorInfo, AppriseVmConfig, AppriseVmOptions } from '@/types'
import { buildDefaultAppriseConfig, FALLBACK_APPRISE_OPTIONS } from './appriseDefaults'
import { imageRefString, PATCH_MODE_OPTIONS, DEFAULT_IMAGE, DEFAULT_IMAGE_LABEL, sameImage } from './appriseCatalog'
import { credentialSummary, storedSecretName } from './appriseCredentials'

export interface AppriseReviewRow {
  label: string
  value: string
  mono?: boolean
}

export interface AppriseReviewGroup {
  title: string
  rows: AppriseReviewRow[]
}

export interface AppriseReviewContext {
  options?: Pick<AppriseVmOptions, 'images'>
  /** Pinned subscription name. */
  subscription?: string
  /** Region as shown to the user. */
  region?: string
  /** "2 vCPU, 8 GiB" for the chosen size when known. */
  sizeSummary?: string
}

/** Who may approve the plan, from the executor info (self approval is a local, development-only switch). */
export function approverSentence(executor: Pick<AppriseExecutorInfo, 'self_approval_enabled'> | undefined): string {
  return executor?.self_approval_enabled
    ? 'An administrator must approve the plan — in this local environment that can be you if you are an administrator.'
    : 'An administrator other than the requester must review and approve the plan on the deployment page.'
}

/** OS disk size for summaries: null is the image's default size. */
export const osDiskLabel = (gb: number | null): string => (gb == null ? 'Image default' : `${gb} GB`)

const onOff = (b: boolean) => (b ? 'Enabled' : 'Disabled')
const orDash = (s: string) => (s && s.trim() ? s : '—')
const lastSegment = (id: string) => id.split('/').filter(Boolean).pop() ?? id
const NOT_SUPPORTED = 'Off — not supported by the portal yet'

export const OS_LABELS: Record<string, string> = { windows: 'Windows', linux: 'Linux', '': 'Not chosen' }
export const ACCESS_LABELS: Record<string, string> = {
  managed_outside: 'Managed outside the portal — no inbound port is opened',
  azure_bastion: 'Azure Bastion', private_network: 'Private network (source ranges)', jit: 'Just-in-time (Defender for Servers)',
}
export const IDENTITY_LABELS: Record<string, string> = {
  system_assigned: 'System-assigned', user_assigned: 'User-assigned', none: 'None',
}
export const OUTBOUND_LABELS: Record<string, string> = {
  internet_allowed: 'Internet allowed', nat_gateway: 'NAT gateway', restricted: 'Restricted',
}
export const ENVIRONMENT_LABELS: Record<string, string> = { development: 'Development', staging: 'Staging', production: 'Production' }
export const DISK_TYPE_LABELS: Record<string, string> = {
  Premium_LRS: 'Premium SSD (locally-redundant storage)', StandardSSD_LRS: 'Standard SSD (locally-redundant storage)',
  Standard_LRS: 'Standard HDD (locally-redundant storage)', Premium_ZRS: 'Premium SSD (zone-redundant storage)',
  StandardSSD_ZRS: 'Standard SSD (zone-redundant storage)',
}
export const SECURITY_TYPE_LABELS: Record<string, string> = {
  standard: 'Standard', trusted_launch: 'Trusted launch virtual machines', confidential: 'Confidential virtual machines',
}

function imageLabel(cfg: AppriseVmConfig, options?: Pick<AppriseVmOptions, 'images'>): string {
  const i = cfg.server.image
  if (!i.publisher) return '—'
  const known = options?.images.find((x) => sameImage(x, i))
  const label = known?.label ?? (sameImage(DEFAULT_IMAGE, i) ? DEFAULT_IMAGE_LABEL : '')
  const ref = imageRefString(i)
  return label ? `${label} (${ref})` : ref
}

function availabilityLabel(a: AppriseVmConfig['server']['availability']): string {
  switch (a.type) {
    case 'zone': return `Availability zone ${a.zone || '—'}`
    case 'availability_set': return `Availability set: ${lastSegment(a.availability_set_id)}`
    case 'vmss': return `Virtual machine scale set: ${lastSegment(a.vmss_id)}`
    default: return 'No infrastructure redundancy required'
  }
}

function ruleSummary(r: AppriseVmConfig['network']['inbound_rules'][number]): string {
  return `${r.name} · ${r.protocol.toUpperCase()} ${r.port} from ${r.source_cidr} · priority ${r.priority}`
}

function licenseLabel(s: AppriseVmConfig['server']): string {
  if (s.os_type !== 'windows') return 'Not applicable (Linux image)'
  return s.license_type === 'windows_server'
    ? `Azure Hybrid Benefit — existing Windows Server license${s.license_confirmed ? ' (eligibility confirmed by the requester)' : ' (NOT confirmed)'}`
    : 'Not using Azure Hybrid Benefit (Windows license billed pay-as-you-go)'
}

function networkRows(cfg: AppriseVmConfig): AppriseReviewRow[] {
  const n = cfg.network
  const pip = n.public_ip
  const nsg = n.nsg_mode === 'none' ? 'None' : n.nsg_mode === 'existing'
    ? `Advanced — existing ${n.nsg_name} (${n.nsg_resource_group})` : `Basic — new ${n.nsg_name}, no inbound rules`
  return [
    { label: 'Virtual network', value: n.vnet_mode === 'create_new' ? `${n.vnet_name} (new, ${n.vnet_address_space})` : `${n.vnet_name} (existing, ${n.vnet_resource_group})`, mono: true },
    { label: 'Subnet', value: n.subnet_mode === 'create_new' ? `${n.subnet_name} (new, ${n.subnet_prefix})` : `${n.subnet_name} (existing)`, mono: true },
    { label: 'Private IP', value: n.private_ip_allocation === 'static' ? `Static ${n.private_ip_address}` : 'Dynamic', mono: n.private_ip_allocation === 'static' },
    {
      label: 'Public IP',
      value: pip.mode === 'none' ? 'None (private only)' : pip.mode === 'existing' ? `${pip.name} (existing, ${pip.resource_group})` : `${pip.name} (new, Standard SKU, static)`,
      mono: pip.mode !== 'none',
    },
    ...(pip.mode === 'create_new' && n.dns_label ? [{ label: 'DNS label', value: n.dns_label, mono: true }] : []),
    { label: 'NIC network security group', value: nsg, mono: n.nsg_mode !== 'none' },
    { label: 'Public inbound ports', value: n.inbound_rules.length ? n.inbound_rules.map(ruleSummary).join('\n') : 'None', mono: n.inbound_rules.length > 0 },
    { label: 'Delete public IP and NIC when VM is deleted', value: 'Yes' },
    { label: 'Outbound', value: OUTBOUND_LABELS[n.outbound_mode] ?? n.outbound_mode },
    { label: 'DNS servers', value: n.dns_servers.length ? n.dns_servers.join(', ') : 'Azure-provided', mono: n.dns_servers.length > 0 },
    {
      label: 'Load balancing',
      value: n.load_balancer.mode === 'existing' ? `Existing backend pool: ${lastSegment(n.load_balancer.backend_pool_id)}` : 'None',
      mono: n.load_balancer.mode === 'existing',
    },
  ]
}

/** Every resolved setting, grouped like the Azure portal tabs. */
export function appriseReviewGroups(cfg: AppriseVmConfig, ctx: AppriseReviewContext = {}): AppriseReviewGroup[] {
  const s = cfg.server
  const st = cfg.storage
  const sec = cfg.security
  const o = cfg.operations
  const existing = s.deployment_mode === 'use_existing'
  const windows = s.os_type === 'windows'

  const basics: AppriseReviewRow[] = [
    { label: 'Deployment target', value: 'Azure Virtual Machine' },
    ...(ctx.subscription ? [{ label: 'Subscription', value: ctx.subscription }] : []),
    { label: 'Application SKU', value: cfg.sku },
    { label: 'Mode', value: existing ? 'Use existing server (verify only)' : 'Create new server' },
    {
      label: 'Resource group',
      value: `${s.resource_group_name} (${existing || s.resource_group_mode === 'existing' ? 'existing' : 'will be created'})`,
      mono: true,
    },
    { label: 'Virtual machine name', value: s.server_name, mono: true },
    ...(ctx.region ? [{ label: 'Region', value: ctx.region }] : []),
    { label: 'Operating system', value: OS_LABELS[s.os_type] ?? s.os_type },
  ]
  if (!existing) {
    basics.push(
      { label: windows ? 'Computer name' : 'Hostname', value: s.hostname, mono: true },
      { label: 'Availability options', value: availabilityLabel(s.availability) },
      {
        label: 'Security type',
        value: `${SECURITY_TYPE_LABELS[sec.security_type] ?? sec.security_type}${sec.security_type === 'trusted_launch' ? ` · Secure Boot ${onOff(sec.secure_boot).toLowerCase()} · vTPM ${onOff(sec.vtpm).toLowerCase()}` : ''}`,
      },
      { label: 'Image', value: imageLabel(cfg, ctx.options), mono: true },
      { label: 'Size', value: ctx.sizeSummary ? `${s.vm_size} (${ctx.sizeSummary})` : s.vm_size, mono: true },
      { label: 'Username', value: s.admin_username, mono: true },
      { label: 'Password', value: credentialSummary(sec.credential_mode, sec.credential_secret_name) },
      ...(sec.key_vault_mode !== 'none' ? [{ label: 'Key Vault', value: `${sec.key_vault_name} (${sec.key_vault_mode === 'create_new' ? 'new' : `existing, ${sec.key_vault_resource_group}`})`, mono: true }] : []),
      ...(sec.key_vault_mode !== 'none' && sec.credential_mode !== 'existing_key_vault_secret'
        ? [{ label: 'Credential stored as secret', value: storedSecretName(sec, s.server_name, s.os_type), mono: true }] : []),
      { label: 'Public inbound ports', value: 'None' },
      { label: 'Licensing', value: licenseLabel(s) },
      ...(s.time_zone && windows ? [{ label: 'Time zone', value: s.time_zone }] : []),
      { label: 'Server environment', value: ENVIRONMENT_LABELS[s.server_environment] ?? s.server_environment },
    )
  }
  basics.push({ label: 'Admin access', value: ACCESS_LABELS[s.admin_access_method] ?? s.admin_access_method })
  if (s.admin_access_method === 'private_network') basics.push({ label: 'Admin source ranges', value: orDash(s.admin_source_cidrs.join(', ')), mono: true })
  const groups: AppriseReviewGroup[] = [{ title: 'Basics', rows: basics }]
  if (existing) return groups

  groups.push({
    title: 'Disks',
    rows: [
      { label: 'OS disk size', value: osDiskLabel(st.os_disk_gb) },
      { label: 'OS disk type', value: DISK_TYPE_LABELS[st.os_disk_type] ?? st.os_disk_type },
      { label: 'Delete with VM', value: 'Yes' },
      { label: 'Key management', value: st.disk_encryption_set_id ? `Customer-managed key (${lastSegment(st.disk_encryption_set_id)})` : 'Platform-managed key' },
      { label: 'Encryption at host', value: onOff(st.encryption_at_host) },
      {
        label: 'Data disks',
        value: st.data_disk_required && st.data_disks.length
          ? st.data_disks.map((d, i) => `disk-${String(i + 1).padStart(2, '0')}: ${d.size_gb} GB · ${d.type} · cache ${d.caching}`).join('\n')
          : 'None',
      },
    ],
  })

  groups.push({ title: 'Networking', rows: networkRows(cfg) })

  const bp = st.backup_policy
  groups.push({
    title: 'Management',
    rows: [
      { label: 'Managed identity', value: IDENTITY_LABELS[sec.identity_mode] ?? sec.identity_mode },
      { label: 'Login with Microsoft Entra ID', value: onOff(s.aad_login_enabled) },
      { label: 'Backup', value: st.backup_required ? `${bp.frequency} at ${bp.time} · keep ${bp.retention_days} days · vault ${bp.vault_name} (${bp.vault_mode === 'create_new' ? 'new' : `existing, ${bp.vault_resource_group}`})` : 'Disabled' },
      { label: 'Site recovery', value: NOT_SUPPORTED },
      { label: 'Patch orchestration', value: PATCH_MODE_OPTIONS.find((p) => p.id === o.patch_mode)?.label ?? o.patch_mode },
      { label: 'Periodic assessment', value: onOff(o.periodic_assessment) },
      { label: 'Hotpatch', value: 'Not applicable' },
      { label: 'Defender for Servers check', value: sec.defender_monitoring ? 'Verify and report only' : 'Disabled' },
    ],
  })

  const boot = o.boot_diagnostics
  groups.push({
    title: 'Monitoring',
    rows: [
      { label: 'Recommended alert rules', value: NOT_SUPPORTED },
      {
        label: 'Boot diagnostics',
        value: boot.mode === 'managed' ? 'Enabled with a managed storage account' : boot.mode === 'custom'
          ? `Enabled with custom storage account ${boot.storage_account} (${boot.storage_account_resource_group})` : 'Disabled',
      },
      { label: 'OS guest diagnostics', value: NOT_SUPPORTED },
      { label: 'Application health monitoring', value: NOT_SUPPORTED },
      { label: 'Azure Monitor agent + Log Analytics', value: o.monitoring_enabled ? (o.log_analytics_workspace_id ? 'Enabled — existing workspace' : 'Enabled — new workspace') : 'Disabled' },
    ],
  })

  const tags = Object.entries(o.tags)
  groups.push({
    title: 'Tags',
    rows: [
      ...(tags.length ? tags.map(([k, v]) => ({ label: k, value: v || '—', mono: true })) : [{ label: 'Your tags', value: 'None' }]),
      { label: 'Added automatically', value: 'product, environment, deployment_id, managed_by' },
    ],
  })
  return groups
}

// ─── Defensive reading of stored configs (v1 shapes) ──────────────────────────

type Obj = Record<string, unknown>
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v)

function merge(base: unknown, over: unknown): unknown {
  if (!isObj(base) || !isObj(over)) return over === undefined ? base : over
  const out: Obj = { ...base }
  for (const [k, v] of Object.entries(over)) out[k] = k in base ? merge(base[k], v) : v
  return out
}

/**
 * A stored apprise_vm may predate v2 (availability_zone, public_ip_required, load_balancer_*,
 * boolean boot_diagnostics, patch_management, cost_center / application_owner tags). Map those
 * to the v2 shape and fill any missing key from the defaults so the review never crashes.
 */
export function normalizeAppriseConfig(raw: unknown): AppriseVmConfig {
  const base = buildDefaultAppriseConfig({ environment: 'dev', region: 'East US 2', sku: 'S', options: FALLBACK_APPRISE_OPTIONS })
  if (!isObj(raw)) return base
  const r = structuredClone(raw) as Obj
  const server = isObj(r.server) ? r.server : {}
  const network = isObj(r.network) ? r.network : {}
  const operations = isObj(r.operations) ? r.operations : {}

  if (!isObj(server.availability) && 'availability_zone' in server) {
    const zone = typeof server.availability_zone === 'string' ? server.availability_zone : ''
    server.availability = { type: zone ? 'zone' : 'none', zone, availability_set_id: '', vmss_id: '' }
  }
  if (!isObj(network.public_ip) && 'public_ip_required' in network)
    network.public_ip = { mode: network.public_ip_required ? 'create_new' : 'none', name: '', resource_group: '' }
  if (!isObj(network.load_balancer) && 'load_balancer_required' in network)
    network.load_balancer = { mode: 'none', backend_pool_id: '' }
  if (typeof operations.boot_diagnostics === 'boolean')
    operations.boot_diagnostics = { mode: operations.boot_diagnostics ? 'managed' : 'disabled', storage_account: '', storage_account_resource_group: '' }
  if (isObj(operations.tags)) {
    const tags: Record<string, string> = {}
    for (const [k, v] of Object.entries(operations.tags)) if (typeof v === 'string' && v.trim()) tags[k] = v
    operations.tags = tags
  }
  // A v1 server had no licence option and no security_type: never claim a benefit that was not applied.
  if (!('license_type' in server)) Object.assign(server, { license_type: 'none', license_confirmed: false })
  const security = isObj(r.security) ? r.security : {}
  if (!('security_type' in security)) security.security_type = security.trusted_launch === true ? 'trusted_launch' : 'standard'
  return merge(base, { ...r, server, network, operations, security }) as AppriseVmConfig
}
